import React, { useState, useEffect, useContext } from "react";
import { useDispatch } from "react-redux";
import { Form, Col, Row, Button, Container } from "react-bootstrap";
import { UserContext } from "../../Context/UserContext";
import CustomAlert from "../../Components/CustomAlert/CustomAlert";

interface Option {
  value: string;
  label: string;
}

interface Permissions {
  institution: string[];
  role: string[];
  app: string[];
}

const PermissionsList: React.FC = () => {
  const dispatch = useDispatch();

  // Acceder a los datos del contexto
  const {
    institutions,
    qlikRoles,
    parentApps,
    getInstitutions,
    getQlikRoles,
    getParentApps,
    updatePermissions,
    getPermissions,
    permissionsData,
    getAllUserData,
  } = useContext(UserContext);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertVariant, setAlertVariant] = useState<
    "success" | "danger" | "warning" | "info" | null
  >(null);
  const [selectedInstitutions, setSelectedInstitutions] = useState<Set<string>>(
    new Set()
  );
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<{ [id: string]: any }>({});
  const [permissions, setPermissions] = useState<Permissions>({
    institution: [],
    role: [],
    app: [],
  });
  const [usersData, setUsersData] = useState<any[]>([]);
  const [searchInstitutionTerm, setSearchInstitutionTerm] =
    useState<string>("");
  const [searchRoleTerm, setSearchRoleTerm] = useState<string>("");
  const [searchAppTerm, setSearchAppTerm] = useState<string>("");
  const [searchUserTerm, setSearchUserTerm] =
  useState<string>("");
  // Cargar los datos al inicio
  useEffect(() => {
    const getData = async () => {
      if (!institutions.length) await getInstitutions();
      if (!qlikRoles.length) await getQlikRoles();
      if (!parentApps.length) await getParentApps();
    };

    getData();
  }, []);

  useEffect(() => {
    const getData = async () => {
      if (permissionsData && Object.keys(permissionsData).length > 0) {
        setSelectedInstitutions(
          new Set(permissionsData.institution_id.split(","))
        );
        setSelectedRoles(new Set(permissionsData.role_id.split(",")));
        setSelectedApps(new Set(permissionsData.parent_app_id.split(",")));
        if (permissionsData.users) {
          try {
            const parsedUsers = JSON.parse(permissionsData.users); // convierte string a objeto
      
            // Establecer usuarios seleccionados
            setSelectedUsers(parsedUsers);
      
            // Establecer usersData como lista (array) para usar en el render
            const usersArray = Object.values(parsedUsers);
            setUsersData(usersArray);
          } catch (error) {
            console.error("Error parsing users JSON:", error);
          }
        }
      } else {
        await getPermissions();
//         const response =await getAllUserData(1, '');

//         setUsersData(response.data);
      }
    }
    getData();
  }, [permissionsData]);
  // Manejo de cambios de checkboxes
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const { value, checked } = e.target;

    // Actualiza el estado correctamente dependiendo del tipo
    if (type === "institution") {
      setSelectedInstitutions((prev) => {
        const newSet = new Set(prev);
        checked ? newSet.add(value) : newSet.delete(value);
        return newSet;
      });
    } else if (type === "role") {
      setSelectedRoles((prev) => {
        const newSet = new Set(prev);
        checked ? newSet.add(value) : newSet.delete(value);
        return newSet;
      });
    } else if (type === "app") {
      setSelectedApps((prev) => {
        const newSet = new Set(prev);
        checked ? newSet.add(value) : newSet.delete(value);
        return newSet;
      });
    }else if (type === "user") {
      setSelectedUsers((prev) => {
        const newSelected = { ...prev };
        if (checked) {
          const userObject = usersData.find((u) => u.id.toString() === value);
          if (userObject) {
            newSelected[value] = userObject;
          }
        } else {
          delete newSelected[value];
        }
        return newSelected;
      });
    }
  };

  // Función para actualizar permisos
  const handleUpdatePermissions = async () => {
    const permissionsData = {
      institution_id: Array.from(selectedInstitutions).join(","),
      role_id: Array.from(selectedRoles).join(","),
      parent_app_id: Array.from(selectedApps).join(","),
      users: JSON.stringify(selectedUsers),
    };

    try {
      const response = await updatePermissions(permissionsData);
      if (response.success) {
        setPermissions({
          institution: Array.from(selectedInstitutions),
          role: Array.from(selectedRoles),
          app: Array.from(selectedApps),
        });
        await getPermissions();
        showAlert("Permissions updated successfully", "success");
        setSearchUserTerm("");
      } else {
        showAlert("Error updating permissions", "danger");
      }
    } catch (error) {
      showAlert("Error updating permissions", "danger");
    }
  };

  const cancelPermissions = () => {
    if (permissionsData && Object.keys(permissionsData).length > 0) {
      setSelectedInstitutions(new Set(permissionsData.institution_id.split(",")));
      setSelectedRoles(new Set(permissionsData.role_id.split(",")));
      setSelectedApps(new Set(permissionsData.parent_app_id.split(",")));
      showAlert("Permissions update canceled", "warning");
    } else {
      showAlert("No permissions to restore", "info");
    }
  };
  const showAlert = (
    message: string,
    variant: "success" | "danger" | "warning" | "info"
  ) => {
    setAlertMessage(message);
    setAlertVariant(variant);

    setTimeout(() => {
      setAlertMessage(null);
      setAlertVariant(null);
    }, 5000); // Oculta la alerta después de 5 segundos
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchUserTerm.trim() !== "") {
        const response = await getAllUserData(1, searchUserTerm.trim());
        setUsersData(response.data);
      } else {
        const parsedUsers = JSON.parse(permissionsData.users); // convierte string a objeto
        // Establecer usersData como lista (array) para usar en el render
        const usersArray = Object.values(parsedUsers);
        setUsersData(usersArray);
      }
    }, 2000); // 2000 ms = 2 segundos
  
    return () => clearTimeout(delayDebounce); // Limpia el timeout si el usuario sigue escribiendo
  }, [searchUserTerm]);
  return (
    <>
      <Container className="permissions-container">
      <Row className="permissions-selection-columns">
          {/* Users */}
          <Col md={4} className="permissions-selection-column">
            <label>Users:</label>

            {/* Input de búsqueda */}
            <div className="mx-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search User..."
                value={searchUserTerm}
                onChange={(e) => setSearchUserTerm(e.target.value)}
              />
            </div>

            {/* Lista de checkboxes filtrados */}
            <div className="mx-2 permissions-card">
              {usersData.map((inst: any) => (
                  <Form.Check
                  key={inst.id}
                  type="checkbox"
                  id={inst.id}
                  value={inst.id}
                  label={`${inst.user_firstname} ${inst.user_lastname} (${inst.user_email})`}
                  checked={!!selectedUsers[inst.id]}
                  onChange={(e) => handleCheckboxChange(e, "user")}
                />
                ))}
            </div>
          </Col>
          <Col className="permissions-selection-column d-flex align-items-center justify-content-center" md={8}>
          <div style={{ fontFamily: 'Arial, sans-serif', lineHeight: '1.6' }}>
            <p>
              This module allows you to control access to assets using a flexible, two-layered permission system.
              The first and most direct level is <strong>user-based access</strong>. Here, you can explicitly grant access by selecting individual users.
              These users will have guaranteed access to the assets, regardless of any other permission settings.
            </p>

            <p>
              If a user is not directly selected, access can still be granted through the second layer, which is 
              <strong>rules-based and dynamic</strong>. In this model, users must meet <strong>all three</strong> of the following criteria to gain access:
            </p>

            <ul>
              <li>They must belong to a specific institution.</li>
              <li>They must be assigned a designated role.</li>
              <li>They must have access to a defined application group.</li>
            </ul>

            <p>
              Only when all three conditions are met will access be permitted. Use this permissions setup to tailor access according to 
              the user's organizational structure, ensuring the right users have the right level of access.
            </p>
          </div>
          </Col>

          
        </Row>
        {/* Contenedor para las tres columnas */}
        <Row className="permissions-selection-columns">
          {/* Instituciones */}
          <Col md={4} className="permissions-selection-column">
            <label>Institution:</label>

            {/* Input de búsqueda */}
            <div className="mx-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search institution..."
                value={searchInstitutionTerm}
                onChange={(e) => setSearchInstitutionTerm(e.target.value)}
              />
            </div>

            {/* Lista de checkboxes filtrados */}
            <div className="mx-2 permissions-card">
              {institutions
                .filter((inst: any) =>
                  inst.institution_name
                    .toLowerCase()
                    .includes(searchInstitutionTerm.toLowerCase())
                )
                .map((inst: any) => (
                  <Form.Check
                    key={inst.id}
                    type="checkbox"
                    id={inst.id}
                    value={inst.id}
                    label={inst.institution_name}
                    checked={selectedInstitutions.has(inst.id.toString())}
                    onChange={(e) => handleCheckboxChange(e, "institution")}
                  />
                ))}
            </div>
          </Col>

          {/* Roles */}
          <Col md={4} className="permissions-selection-column">
            <label>Role:</label>
            <div className="mx-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search role..."
                value={searchRoleTerm}
                onChange={(e) => setSearchRoleTerm(e.target.value)}
              />
            </div>

            <div className="mx-2 permissions-card">
              {qlikRoles
                .filter((role: any) =>
                  role.qlik_role_description
                    .toLowerCase()
                    .includes(searchRoleTerm.toLowerCase())
                )
                .map((role: any) => (
                  <Form.Check
                    key={role.id}
                    type="checkbox"
                    id={role.id}
                    value={role.id}
                    label={role.qlik_role_description}
                    checked={selectedRoles.has(role.id.toString())}
                    onChange={(e) => handleCheckboxChange(e, "role")}
                  />
                ))}
            </div>
          </Col>

          {/* Parent Apps */}
          <Col md={4} className="permissions-selection-column">
            <label>Parent App:</label>
            <div className="mx-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Search parent app..."
                value={searchAppTerm}
                onChange={(e) => setSearchAppTerm(e.target.value)}
              />
            </div>

            <div className="mx-2 permissions-card">
              {parentApps
                .filter((app: any) =>
                  app.parent_app_name
                    .toLowerCase()
                    .includes(searchAppTerm.toLowerCase())
                )
                .map((app: any) => (
                  <Form.Check
                    key={app.id}
                    type="checkbox"
                    id={app.id}
                    value={app.id}
                    label={app.parent_app_name}
                    checked={selectedApps.has(app.id.toString())}
                    onChange={(e) => handleCheckboxChange(e, "app")}
                  />
                ))}
            </div>
          </Col>
        </Row>

        {/* Botón para actualizar permisos */}
        <Row className="sticky-footer">
        <div className="d-flex justify-content-end w-100 px-3 py-2 sticky-footer">
            <Button
              variant="outline-primary"
              onClick={cancelPermissions}
              className="update-permissions-button mx-2"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePermissions}
              className="update-permissions-button "
            >
              Update
            </Button>
          </div>
        </Row>
      </Container>
      {alertMessage && (
        <CustomAlert
          message={alertMessage}
          variant={alertVariant!}
          onClose={() => setAlertMessage(null)}
        />
      )}
    </>
  );
};

export default PermissionsList;
